import { api } from '@/api/client'
import type { ExtensionManifest } from '@/extensions/types'

/**
 * Tenant from the host: `<slug>.distribuai.eu|local`, or `<slug>.demo.distribuai.eu` /
 * `<slug>.demo.localhost` on the demo installation; VITE_TENANT_SLUG in local dev.
 */
export function tenantSlugFromHost(hostname: string = window.location.hostname): string | null {
    const match = hostname.match(/^([a-z0-9-]+)\.(?:distribuai\.(?:eu|local)|demo\.distribuai\.eu|demo\.localhost)$/)
    if (match) return match[1]!
    return import.meta.env.VITE_TENANT_SLUG || null
}

/** `GET /api/tenants/settings.php?slug=…` (public): who the tenant is and how it looks. */
export interface TenantSettings {
    success: boolean
    /** "login" before sign-in (branding only), "full" for a signed-in user of this tenant. */
    scope?: 'login' | 'full'
    data: {
        tenant: { id: number; name: string; slug: string; logoUrl?: string | null }
        theme?: {
            defaultLanguage?: string
            availableLanguages?: string[]
            /** Top-bar logo height in px (null: the design's 12 px wordmark size). */
            logoHeight?: number | null
            /** A sales demo: the app shows the DEMO marker. */
            demo?: boolean
            /** Single sign-on providers offered on the sign-in page, e.g. ["microsoft", "google"]. */
            ssoProviders?: string[]
            /** The tenant's own main menu: view ids in order (signed in only; see tenantMenu.ts). */
            menu?: string[]
        }
    }
}

export function fetchTenantSettings(slug: string, signal?: AbortSignal) {
    return api.get<TenantSettings>(`/api/tenants/settings.php?slug=${encodeURIComponent(slug)}`, { signal, handleUnauthorized: false })
}

/** `GET /api/auth/me.php`: the signed-in user, or 401. */
export interface SessionInfo {
    success: boolean
    data: {
        user_id: number
        email: string
        full_name: string
        tenant_id: number
        tenant_slug: string
        role_name: string
        csrf_token: string | null
        expires_at: number
    }
}

export function fetchSession(signal?: AbortSignal) {
    return api.get<SessionInfo>('/api/auth/me.php', { signal, handleUnauthorized: false })
}

/**
 * The tenant's current extension (served behind the login check). 404 = the tenant
 * has no extension, which is fine.
 */
export async function fetchExtensionManifest(slug: string, signal?: AbortSignal): Promise<ExtensionManifest | null> {
    try {
        const manifest = await api.get<ExtensionManifest>(`/ext/${slug}/current.json`, { signal })
        return { ...manifest, url: new URL(manifest.url, new URL(`/ext/${slug}/`, window.location.origin)).pathname }
    } catch (error) {
        if (error instanceof Error && 'status' in error && (error as { status: number }).status === 404) return null
        throw error
    }
}
